import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";

import { Module, PATH_PAYROLL_INSPECTION } from "@/constants";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { updatePayrollDepartmentBasedInspection } from "@/schemas/payrollDepartmentBasedInspection";
import { updatePayrollRoleBasedInspection } from "@/schemas/payrollRoleBasedInspection";
import { PayrollsService } from "@/services/payrolls";
import { PayrollDepartmentBasedInspection } from "@/types/payrollDepartmentBasedInspection";
import { PayrollRoleBasedInspection } from "@/types/payrollRoleBasedInspection";
import { z } from "@/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import { useUpdatePayrollInspectionMutation } from "../hooks/useUpdatePayrollInspectionMutation";
import {
  updatePayrollDepartmentBasedInspectionDetailsFormSchema,
  updatePayrollRoleBasedInspectionDetailsFormSchema,
} from "../schema";
import { PayrollInspectionDepartmentBasedSections } from "./PayrollInspectionDepartmentBasedSections";
import { PayrollInspectionRecordView } from "./PayrollInspectionRecordView";
import { PayrollInspectionRoleBasedSections } from "./PayrollInspectionRoleBasedSections";

type FormData = z.infer<
  | typeof updatePayrollRoleBasedInspection
  | typeof updatePayrollDepartmentBasedInspection
>;

const roleDefaultValues = {
  settlement_type: null,
  hours_worked_corrected: null,
  visit_count_corrected: null,
  deduction: null,
  penalty: null,
  addition: null,
  bonus: null,
  comment: null,
  tax_status_corrected: null,
};

const deptDefaultValues = {
  hours_worked_corrected: null,
  deduction: null,
  penalty: null,
  cash_shortage: null,
  addition: null,
  bonus: null,
  comment: null,
  tax_status_corrected: null,
};

export const PayrollInspectionDetail = memo(() => {
  const { t } = useTranslation();
  const { payrollId, entryId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const isRoleBased = isProjectRoleDependent(payrollQuery.data);
  const permissions = usePayrollPermissions();
  const mutation = useUpdatePayrollInspectionMutation(
    payrollQuery.data?.project_depends_on,
  );

  const formSchema = isRoleBased
    ? updatePayrollRoleBasedInspectionDetailsFormSchema
    : updatePayrollDepartmentBasedInspectionDetailsFormSchema;

  const inspectionTitle = t(
    permissions.canUpdateInspection
      ? "payrolls.views.inspection.title"
      : "payrolls.views.inspection.titleReadOnly",
  );

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: inspectionTitle,
      href: generatePath(PATH_PAYROLL_INSPECTION, {
        payrollId,
      }),
    },
    {
      label: `${t("payrolls.details.title")}`,
    },
  ]);

  const entryQuery = useQuery<
    PayrollRoleBasedInspection | PayrollDepartmentBasedInspection
  >({
    queryKey: [Module.Payrolls, payrollId, "inspection", entryId],
    queryFn: () =>
      PayrollsService.getInspection(
        entryId,
        payrollQuery.data?.project_depends_on,
      ),
    enabled: payrollQuery.isSuccess,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isRoleBased ? roleDefaultValues : deptDefaultValues,
    disabled: !permissions.canUpdateInspection || mutation.isPending,
  });

  useEffect(() => {
    if (entryQuery.data === undefined) {
      return;
    }
    const { id, ...rest } = entryQuery?.data || {};
    form.reset(rest);
  }, [entryQuery.data, form]);

  const handleSubmit = useCallback(
    (values: FormData) => {
      return mutation.mutateAsync(values);
    },
    [mutation],
  );

  if (payrollQuery.isLoading || entryQuery.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />
      <Title>{inspectionTitle}</Title>
      <PayrollInspectionRecordView />
      <Title level={4}>{t("payrolls.details.title")}</Title>

      <Form form={form} handleSubmit={handleSubmit}>
        {isRoleBased && (
          <PayrollInspectionRoleBasedSections
            entryQuery={
              entryQuery as UseQueryResult<PayrollRoleBasedInspection>
            }
          />
        )}
        {!isRoleBased && (
          <PayrollInspectionDepartmentBasedSections
            entryQuery={
              entryQuery as UseQueryResult<PayrollDepartmentBasedInspection>
            }
          />
        )}
        <footer className="flex py-2 justify-end gap-6 mt-6">
          {permissions.canUpdateInspection && <SaveButton />}
        </footer>
      </Form>
    </>
  );
});

PayrollInspectionDetail.displayName = "PayrollInspectionDetail";
